import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InputField from "./InputField";
import DropdownField from "./DropdownField";
import CheckboxField from "./CheckBoxField";
import YesNoField from "./YesNoField";
import RadioField from "./RadioField";
import './NewFormRenderer.css';
import statesCitiesData from "../data/statesCitiesData";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const NewFormRenderer = ({ schema, onSubmit }) => {
    const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: {
            oshaViolations: "false",
            siteSafetyMeetings: "false",
            generalAddress: {
                state: "",
                city: "",
            },
        },
        mode: "onBlur",
    });

    const watchValues = watch();
    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);
    const [expanded, setExpanded] = useState("generalInformation");
    const sectionRefs = useRef({});
    const [formKey, setFormKey] = useState(0);

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files); // Convert FileList to Array
        setAttachments((prevFiles) => [...prevFiles, ...files]); // Append new files to existing ones
    };

    const handleFileDelete = (index) => {
        const updatedFiles = attachments.filter((_, i) => i !== index);
        setAttachments(updatedFiles);

        // Clear the file input if no files remain
        if (updatedFiles.length === 0 && fileInputRef.current) {
            fileInputRef.current.value = ""; // Reset the input value
        }
    };

    const handleAccordionChange = (sectionName) => (event, isExpanded) => {
        setExpanded(isExpanded ? sectionName : false);

        if (isExpanded) {
            // Scroll to the top of the expanded section
            setTimeout(() => {
                sectionRefs.current[sectionName].scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }, 300);
        }
    };

    const renderField = (field) => {
        const { name, type, label, options, validation, fileLink } = field;

        const commonProps = { name, label, control, rules: validation };

        switch (type) {
            case "text":
                return <InputField key={name} {...commonProps} />;
            case "textarea":
                return (
                    <div key={name} className="form-group">
                        <label>
                            {label}
                            {validation?.required && <span style={{ color: "red" }}> *</span>}
                        </label>
                        <textarea
                            {...control.register(name, validation)}
                            value={watch(name) || ""}
                            rows={3}
                            className={`textarea-field ${errors[name] ? "error" : ""}`}
                        />
                        {errors[name] && <span style={{ color: "red", fontSize: "12px" }}>{errors[name].message || "This field is required"}</span>}
                    </div>
                );
            case "dropdown":
                if (name === "state" || name === "safetyState" || name === "bondingState") {
                    return (
                        <DropdownField
                            key={name} {...commonProps}
                            options={statesCitiesData.states}
                        />
                    );
                }
                return <DropdownField key={name} {...commonProps} options={options} />;

            case "radio-group":
                return (
                    <div key={name}>
                        <RadioField
                            key={name}
                            {...commonProps}
                            options={options}
                            renderRelatedFields={(option) =>
                                option.value === "corporation" &&
                                option.relatedFields?.map((relatedField, index) =>
                                    renderField({ ...relatedField, key: `${relatedField.name}-${index}` })
                                )
                            }
                        />
                    </div>
                );

            case "inline-group":
                return (
                    <div key={field.name} className="form-group inline-group">
                        {field.label && <label className="inline-group-label">{field.label}</label>}
                        <div className="inline-group-items">
                            {field.fields.map((subField) => (
                                <div key={subField.name} className="inline-group-item">
                                    {renderField(subField)}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            case "yesNo":
                return <YesNoField key={name} {...commonProps} />;
            case "checkbox-pair-group":
                return (
                    <div key={name} className="form-group checkbox-pair-group">
                        <div className="inline-checkbox-group">
                            {options.map((option, index) => (
                                <div key={option.value} className="checkbox-pair">
                                    <CheckboxField
                                        key={option.value}  // Add key here as well for each checkbox pair
                                        name={`${name}[${index}]`}
                                        label={option.label}
                                        control={control}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case "text-info":
                return (
                    <div key={name} className="form-group">
                        <p>{label}</p>
                    </div>
                );

            case "text-info-note":
                return (
                    <div key={name} className="form-group">
                        <p className="text-info-bold-label">{label}</p>
                    </div>
                );

            case "file-download":
                return (
                    <div key={name} className="form-group">
                        <a
                            href={fileLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-link"
                        >
                            {label}
                        </a>
                    </div>
                );

            case "upload-file":
                /* Render the file upload section after accordion sections */
                return (
                    <div className="form-group file-upload-group">
                        <label>
                            Upload Supporting Documents<span style={{ color: "red" }}> *</span>
                        </label>
                        <input
                            type="file"
                            multiple
                            onChange={handleFileUpload}
                            ref={fileInputRef}
                            className={`file-upload-input ${attachments.length === 0 ? "error" : ""}`} // Add error class for styling
                        />
                        {attachments.length > 0 && (
                            <ul className="uploaded-files-list">
                                {attachments.map((file, index) => (
                                    <li key={index} className="uploaded-file-item">
                                        {file.name}
                                        <button
                                            type="button"
                                            className="delete-file-button"
                                            onClick={() => handleFileDelete(index)}
                                        >
                                            ✖
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {attachments.length === 0 && (
                            <p style={{ color: "red", fontSize: "0.9em" }}>
                                You must upload at least one file to submit the form.
                            </p>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    // Conditional rendering logic
    const conditionalRender = (field) => {
        const { name } = field;

        // Define field-specific conditions based on watched values
        const conditions = {
            oshaViolationsCorrectiveMeasures: watchValues.oshaViolations === "true",
            howOften: watchValues.siteSafetyMeetings === "true",
        };

        // Return the field only if its condition is true or not explicitly defined
        if (conditions.hasOwnProperty(name)) {
            return conditions[name] ? renderField(field) : null;
        }
        return renderField(field);
    };

    const renderSectionWithoutAccordion = (section) => {
        return (
            <div key={section.name} className="form-section">
                {section.label && <h3>{section.label}</h3>}
                {section.fields.map((field) => conditionalRender(field))}
            </div>
        );
    };

    const renderSectionWithAccordion = (section) => {
        return (
            <Accordion
                id={section.name}
                key={section.name}
                expanded={expanded === section.name}
                onChange={handleAccordionChange(section.name)}
                ref={(el) => (sectionRefs.current[section.name] = el)}
                sx={{
                    marginBottom: '12px', // Add space between accordions
                    border: '1px solid #ccc', // Border for accordion
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)', // Subtle shadow
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: 'lightgrey', // Light gray background
                        '&:hover': {
                            backgroundColor: 'silver', // Darker gray on hover
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 'bold', // Bold font
                            color: '#333', // Dark text color
                        }}
                    >
                        {section.label}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {section.fields.map((field) => conditionalRender(field))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderSection = (section) => {
        const isNonAccordionSection = ["insurance", "uploadsRequired", "downloadsAvailable"].includes(section.name);

        if (isNonAccordionSection) {
            return renderSectionWithoutAccordion(section);
        }

        return renderSectionWithAccordion(section);
    };


    const onSubmitHandler = async (data) => {
        // Check if bondingFiles is empty
        if (attachments.length === 0) {
            toast.error("Please upload at least one file before submitting the form."); // User feedback
            return; // Prevent form submission
        }

        // Include bonding files in the submission data
        const submissionData = {
            ...data,
            bondingFiles: attachments,
        };

        // Call the parent onSubmit function and wait for its result
        const isSuccessful = await onSubmit(submissionData);

        if (isSuccessful) {
            // Show success message
            toast.success("Form submitted successfully!");
            reset(); // Reset the form state
            setAttachments([]); // Clear the attachments
            setFormKey((prevKey) => prevKey + 1); // Force form re-render
            setExpanded("generalInformation");

            // Scroll to the General Information section
            setTimeout(() => {
                sectionRefs.current["generalInformation"]?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 300);
        } else {
            // Keep the form state intact on failure
            toast.error("Form submission failed. Please try again.");
        }
    };

    const onInvalidHandler = (errors) => {
        validateForm(errors);
    };

    const validateForm = (errors) => {
        const firstErrorField = Object.keys(errors)[0];

        // Flatten fields within a section
        const flattenFields = (section) => {
            return section.fields.flatMap((field) =>
                field.type === "inline-group" ? field.fields : field
            );
        };

        // Find the section containing the error field
        const firstErrorSection = schema.sections.find((section) =>
            flattenFields(section).some((field) => field.name === firstErrorField)
        );

        if (firstErrorSection) {
            // Scroll to the accordion section containing the first error
            setTimeout(() => {
                sectionRefs.current[firstErrorSection.name].scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 300);

            // Expand the accordion containing the error
            setExpanded(firstErrorSection.name);
        }

        toast.error("Please correct the highlighted errors before submitting.");
    };

    return (
        <form key={formKey} className="form-renderer" onSubmit={handleSubmit(onSubmitHandler, onInvalidHandler)}>
            {schema.sections.map((section, index) => {
                return renderSection(section);
            })}

            <Button variant="contained" color="primary" type="submit" className="submit-button">
                Submit
            </Button>
        </form>
    );
};

export default NewFormRenderer;
